import { useMemo, useEffect, useCallback } from 'react';
import { createBrowserRouter, RouterProvider, useLocation, useNavigate, useRoutes } from 'react-router';
import type { RouteObject } from 'react-router';
import BasicLayout from '../layout/basic/BasicLayout';
import { RedirectToRouteComponent } from './redirectToRoute.component';
import { RouterPaths } from './RouterPaths';
import { observer } from 'mobx-react';
import { RouteParams, routes } from './routes.set';
import { useStores } from '../hooks';
import { SpotLoading } from '@enterprise/spot';
import { getAuth0Path } from './postLogin.utils';

const RouteGate = observer(function RouteGateComponent({ route }: { route: RouteParams }) {
    const {
        ui: { app },
    } = useStores();

    const isAllowedRoute = useCallback(
        ({ isSecured, role: expectedRoles }: RouteParams): boolean => {
            if (!isSecured) {
                return true;
            }

            if (!app.isAuthenticated) {
                return false;
            }

            return !expectedRoles || Boolean(Array.from(expectedRoles).find((role) => app.token.hasRole(role)));
        },
        [app.isAuthenticated, app.token],
    );

    const { Component, config } = route;
    const Layout = route.Layout ? route.Layout : BasicLayout;
    const isAllowed = isAllowedRoute(route);

    if (!isAllowed) {
        const redirectPath = !app.isAuthenticated && route.isSecured ? getAuth0Path() : config?.secureRedirectTo || RouterPaths.BasePages.Root;
        return <RedirectToRouteComponent to={redirectPath} />;
    }

    return (
        <Layout {...config}>
            <Component {...config} />
        </Layout>
    );
});

const RoutesShell = observer(function RoutesShellComponent() {
    const {
        ui: { app, router },
    } = useStores();

    const navigate = useNavigate();
    const location = useLocation();

    const isLoading = !app.isLoaded || !router.location || !router.navigate;

    useEffect(() => {
        router.navigate = navigate;
    }, [navigate]);

    useEffect(() => {
        router.location = location;
    }, [location.pathname, location.search]);

    const childRoutes = useMemo((): RouteObject[] => {
        return routes.map((route) => {
            const isIndex = route.path === RouterPaths.BasePages.Root && Boolean(route.exact);
            const pathPattern = `${route.path ?? ''}${route.exact ? '' : '/*'}`;

            if (isIndex) {
                return { index: true, element: <RouteGate route={route} /> };
            }

            return { path: pathPattern, element: <RouteGate route={route} /> };
        });
    }, [location.pathname, location.search, routes]);

    const element = useRoutes(childRoutes);

    return (
        <>
            {isLoading && <SpotLoading />}
            {!isLoading && element}
        </>
    );
});

export { RoutesShell };

export const ConnectRouter = () => {
    const browserRouter = useMemo(
        () =>
            createBrowserRouter([
                {
                    path: '*',
                    Component: RoutesShell,
                },
            ]),
        [],
    );

    return <RouterProvider router={browserRouter} />;
};
